import { useRef, useState } from 'react'
import { session } from '../api.js'
import { Banner, Button, Card, ErrorState, Loading, Metric, MetricRow, SectionHeader, Table } from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Crane master import: template, dry run, then commit.
 *
 * **The one tool here that genuinely needed porting rather than a form.** A multipart upload, a diff to render
 * before anything is written, and only then a commit — which is why it was the last of the four. It is also the
 * tool that loads your real fleet, so the sequence matters more than the styling.
 *
 * Dry run is not a checkbox the user may forget: the server defaults `dryRun=true` so a mis-clicked upload
 * previews, and this screen refuses to offer Commit until a dry run of *that same file* has come back clean.
 * The importer's own guidance is "always dry-run first"; the UI enforces it rather than advising it.
 *
 * `fetch` is used directly rather than the shared `api()` helper because this is multipart, not JSON — the
 * helper sets a JSON content type, and setting one by hand on a FormData body breaks the boundary. The bearer
 * token comes from the same session either way, so there is still one sign-in.
 */
export default function CraneImport() {
  const audits = useApi('/admin/imports')
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)   // dry-run ImportResult for the CURRENT file
  const [committed, setCommitted] = useState(null)
  const [busy, setBusy] = useState(null)         // 'dry' | 'commit' | null
  const [error, setError] = useState(null)

  function choose(e) {
    const f = e.target.files?.[0] || null
    setFile(f)
    // Any previous preview belongs to a different file. Keeping it would let someone dry-run file A and
    // commit file B — the one mistake this screen exists to prevent.
    setPreview(null)
    setCommitted(null)
    setError(null)
  }

  async function upload(dryRun) {
    if (!file) return
    setBusy(dryRun ? 'dry' : 'commit')
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`/api/v1/admin/imports/assets?dryRun=${dryRun}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.token}` },
        body: form,
      })
      if (!res.ok) {
        let detail
        try { detail = await res.json() } catch { /* not JSON */ }
        throw new Error(detail?.message || `Upload failed (${res.status}).`)
      }
      const result = await res.json()
      if (dryRun) setPreview(result)
      else {
        setCommitted(result)
        setPreview(null)
        setFile(null)
        if (fileRef.current) fileRef.current.value = ''
        audits.reload()
      }
    } catch (e) {
      setError(e)
    } finally {
      setBusy(null)
    }
  }

  // Commit is offered only when this exact file previewed without a file-level error. `ok` is false when any
  // error has a null row, which the server defines as "nothing imported".
  const canCommit = Boolean(preview?.ok) && preview.rowsRejected === 0

  return (
    <>
      <PageHeader
        title="Crane master import"
        subtitle="Upload the Excel template. Dry run first — the preview shows exactly what would change."
        actions={
          <a className="btn" href="/api/v1/admin/imports/assets/template.xlsx">Download template</a>
        }
      />

      <Banner kind="info">
        The template is generated from the live schema, so what you download always matches what the parser
        accepts. Crane number is the key: unknown adds a crane, known updates it in place.
      </Banner>

      {error && <Banner kind="danger">{error.message}</Banner>}

      <Card title="1 · Choose the file">
        <div className="row">
          <input ref={fileRef} type="file" accept=".xlsx" onChange={choose} aria-label="Excel file" />
          <Button variant="secondary" disabled={!file || busy} onClick={() => upload(true)}>
            {busy === 'dry' ? 'Checking…' : 'Dry run'}
          </Button>
        </div>
        {file && <p className="muted">{file.name} · {(file.size / 1024).toFixed(0)} kB</p>}
      </Card>

      {preview && (
        <Card
          title="2 · What would change"
          subtitle="Nothing has been written yet"
          accent={preview.rowsRejected > 0 ? 'danger' : undefined}
        >
          <MetricRow>
            <Metric label="New cranes" value={preview.rowsInserted} kind={preview.rowsInserted ? 'success' : undefined} />
            <Metric label="Updated" value={preview.rowsUpdated} kind={preview.rowsUpdated ? 'warning' : undefined} />
            <Metric
              label="Rejected"
              value={preview.rowsRejected}
              kind={preview.rowsRejected ? 'danger' : 'success'}
              hint={preview.rowsRejected ? 'Fix these first' : 'Nothing rejected'}
            />
            <Metric label="Sheets read" value={preview.sheetsSeen?.length ?? 0} hint={(preview.sheetsSeen || []).join(', ')} />
          </MetricRow>

          {preview.errors?.length > 0 && (
            <>
              <SectionHeader hint="Every one of these blocks the row it names">Problems found</SectionHeader>
              <Table
                keyOf={(e, i) => `${e.sheet}-${e.row}-${e.column}-${i}`}
                columns={[
                  { key: 'sheet', header: 'Sheet' },
                  { key: 'row', header: 'Row', numeric: true, render: (e) => e.row ?? <span className="muted">file</span> },
                  { key: 'craneNo', header: 'Crane no', render: (e) => e.craneNo || <span className="muted">—</span> },
                  { key: 'column', header: 'Column', render: (e) => e.column || <span className="muted">—</span> },
                  { key: 'message', header: 'Problem' },
                ]}
                rows={preview.errors}
              />
              {preview.errors.some((e) => e.row === null) && (
                <Banner kind="danger">
                  A file-level problem means nothing would import. Fix it and dry-run again.
                </Banner>
              )}
            </>
          )}

          {preview.outcomes?.length > 0 && (
            <>
              <SectionHeader hint="Row by row">Planned changes</SectionHeader>
              <Table
                keyOf={(o, i) => `${o.craneNo}-${i}`}
                columns={[
                  { key: 'craneNo', header: 'Crane no', render: (o) => <strong>{o.craneNo}</strong> },
                  {
                    key: 'action',
                    header: 'Action',
                    render: (o) => (
                      <span className={o.action === 'REJECTED' ? 'val-bad' : o.action === 'INSERT' ? 'val-good' : undefined}>
                        {titleCase(o.action)}
                      </span>
                    ),
                  },
                  { key: 'detail', header: 'Detail', render: (o) => o.detail || <span className="muted">—</span> },
                ]}
                rows={preview.outcomes}
              />
            </>
          )}

          <SectionHeader>3 · Commit</SectionHeader>
          {canCommit ? (
            <>
              <p className="muted">
                This writes {preview.rowsInserted} new and {preview.rowsUpdated} updated crane
                {preview.rowsInserted + preview.rowsUpdated === 1 ? '' : 's'}. The same file is re-parsed on
                commit, so what you reviewed is what gets written.
              </p>
              <Button disabled={busy} onClick={() => upload(false)}>
                {busy === 'commit' ? 'Importing…' : `Import ${preview.rowsInserted + preview.rowsUpdated} cranes`}
              </Button>
            </>
          ) : (
            <Banner kind="warning">
              {preview.rowsRejected > 0
                ? `${preview.rowsRejected} row${preview.rowsRejected === 1 ? '' : 's'} would be rejected. Fix the file and dry-run again — importing a partial fleet leaves you unsure what is missing.`
                : 'Dry run this file before importing.'}
            </Banner>
          )}
        </Card>
      )}

      {committed && (
        <Card title="Imported" accent={committed.rowsRejected ? 'warning' : undefined}>
          <MetricRow>
            <Metric label="Added" value={committed.rowsInserted} kind="success" />
            <Metric label="Updated" value={committed.rowsUpdated} kind="success" />
            <Metric label="Rejected" value={committed.rowsRejected} kind={committed.rowsRejected ? 'danger' : undefined} />
          </MetricRow>
          <p className="muted">{committed.filename} · written to the fleet.</p>
        </Card>
      )}

      <SectionHeader hint="Dry runs included, newest first">Import history</SectionHeader>
      <Card>
        {audits.loading && <Loading rows={4} />}
        {audits.error && <ErrorState error={audits.error} onRetry={audits.reload} />}
        {audits.data && (
          <Table
            keyOf={(a) => a.id}
            columns={[
              { key: 'at', header: 'When', render: (a) => fmtDate(a.at) },
              { key: 'filename', header: 'File' },
              {
                key: 'dryRun',
                header: 'Kind',
                render: (a) => (a.dryRun ? <span className="muted">Dry run</span> : <strong>Import</strong>),
              },
              { key: 'rowsInserted', header: 'Added', numeric: true },
              { key: 'rowsUpdated', header: 'Updated', numeric: true },
              {
                key: 'rowsRejected',
                header: 'Rejected',
                numeric: true,
                render: (a) => (a.rowsRejected ? <span className="val-bad">{a.rowsRejected}</span> : '0'),
              },
            ]}
            rows={Array.isArray(audits.data) ? audits.data : []}
            empty={<p className="muted">Nothing imported yet.</p>}
          />
        )}
      </Card>
    </>
  )
}

const titleCase = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : '—')

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
